import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import { useGooglePlacesAutocomplete } from "../../hooks/useGooglePlacesAutocomplete";
import "./PackageCatalogue.css";

/**
 * ALL PACKAGES — the catalogue (rebuilt 2026-10-23).
 *
 * WHAT WAS WRONG. This page replaced `PublicPackages.js`, which fetched
 * `/api/packages/featured?limit=50` and then filtered and sorted in the BROWSER. `featured` answers a
 * different question — "the packages a shop chose to advertise", for the home-page strip — so the
 * catalogue was capped, unsearchable and impossible to page past 50. A card also showed no shop, no
 * per-session value and no statement of what was covered. That is what made it read as broken.
 *
 * POSITIONING: "packages near you / from shops you've used". The second half is deliberately NOT
 * "shops you follow" — following in this codebase is for professionals (`Follow.targetStaffId`), so a
 * followed-shops filter would have had no data behind it. What a customer demonstrably has is places
 * they have booked or bought from, and the server answers that from `mine=1`.
 *
 * THE FILTERS LIVE IN THE URL. A filtered view has to be reloadable and shareable — the same lesson
 * the Inventory reports page learned when its tabs were local state and nobody could link to them.
 */

const PAGE_SIZE = 12;

function readParam(searchParams, key, fallback = "") {
  return searchParams.get(key) || fallback;
}

/** `$12.50` — used for both the package price and the per-session figure. */
function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function PackageCard({ pkg, onOpen }) {
  const business = pkg.businessId || {};
  const services = Array.isArray(pkg.services) ? pkg.services : [];
  const sessions = Number(pkg.totalSessions) || 0;
  const perSession = sessions > 0 ? Number(pkg.packagePrice) / sessions : null;
  const shops = Array.isArray(pkg.availableAtShops) ? pkg.availableAtShops : [];
  const where = shops.length
    ? [...new Set(shops.map((shop) => shop.city).filter(Boolean))].slice(0, 2).join(" · ")
    : "";

  return (
    <article className="pk-card">
      <div className="pk-card-head">
        <span className="pk-card-shop">{business.name || "A shop on OGESTYLE"}</span>
        {where ? <span className="pk-card-where">{where}</span> : null}
      </div>

      <h3 className="pk-card-title">{pkg.name || pkg.packageName || "Package"}</h3>

      {services.length > 0 ? (
        <p className="pk-card-services">
          {services
            .map((row) => row.serviceId?.name || row.serviceName)
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}

      <div className="pk-card-meta">
        <span>
          {sessions} session{sessions === 1 ? "" : "s"}
        </span>
        {pkg.validityPeriod?.value ? (
          <span>
            {pkg.validityPeriod.value} day{pkg.validityPeriod.value === 1 ? "" : "s"} to use
          </span>
        ) : null}
        {pkg.distanceLabel ? <span>{pkg.distanceLabel} away</span> : null}
      </div>

      <div className="pk-card-foot">
        <div>
          <div className="pk-card-price">{money(pkg.packagePrice)}</div>
          {perSession ? (
            <div className="pk-card-persession">{money(perSession)} per session</div>
          ) : null}
        </div>
        <button type="button" className="pk-card-cta" onClick={() => onOpen(pkg)}>
          View package
        </button>
      </div>
    </article>
  );
}

function PackageCatalogue() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Controls are local state so typing does not fire a request per keystroke; the URL is written when
  // a value is applied (search on submit, selects immediately).
  const [query, setQuery] = useState(readParam(searchParams, "q"));
  const [sort, setSort] = useState(readParam(searchParams, "sort", "newest"));
  const [mine, setMine] = useState(readParam(searchParams, "mine") === "1");
  const [page, setPage] = useState(Number(readParam(searchParams, "page", "1")) || 1);
  const [radius, setRadius] = useState(readParam(searchParams, "radius", ""));
  const [radiusOptions, setRadiusOptions] = useState([5, 10, 25, 50]);
  // The admin-configured default, kept so "Clear filters" returns to the setting rather than a literal.
  const [defaultRadius, setDefaultRadius] = useState("");

  /**
   * LOCATION, THE SAME WAY THE OTHER BROWSE PAGES DO IT.
   *
   * This is the shared hook `/shops` uses — browser geolocation first, a Places autocomplete for a
   * typed city second — so the permission prompt and the suggestion list behave identically here.
   * Its `locationSource` is what tells us whether the visitor picked a city by hand (in which case
   * we send `city`/`state` so the server narrows before it measures).
   */
  const {
    inputValue: locationInput,
    suggestions,
    selectedCoords,
    locationSource,
    geolocationStatus,
    showSuggestions,
    handleInputChange: handleLocationInput,
    selectSuggestion,
    clearLocation,
    setShowSuggestions,
  } = useGooglePlacesAutocomplete();

  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isSignedIn = Boolean(localStorage.getItem("ogestyle_token"));
  const apiBase = useMemo(() => process.env.REACT_APP_API_BASE_URL, []);

  const headers = useMemo(() => {
    const token = localStorage.getItem("ogestyle_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    if (!apiBase) {
      setError("Configuration error. Please contact support.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${apiBase}/api/packages/public`, {
        params: {
          q: query || undefined,
          sort,
          mine: mine && isSignedIn ? 1 : undefined,
          page,
          limit: PAGE_SIZE,
          // Proximity, when the visitor has granted (or typed) a location.
          ...(selectedCoords
            ? {
                lat: selectedCoords.lat,
                lng: selectedCoords.lng,
                radius,
                ...(locationSource === "manual" && selectedCoords.label
                  ? (() => {
                      const parts = String(selectedCoords.label)
                        .split(",")
                        .map((part) => part.trim());
                      const cityPart = parts[0];
                      const statePart = parts[1]?.split(" ")[0];
                      return {
                        city: cityPart || undefined,
                        state: statePart || undefined,
                      };
                    })()
                  : {}),
              }
            : {}),
        },
        headers,
      });
      setPackages(Array.isArray(res.data?.packages) ? res.data.packages : []);
      setTotal(Number(res.data?.total) || 0);
      setPages(Number(res.data?.pages) || 1);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "We could not load the packages just now. Please try again.",
      );
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, headers, isSignedIn, locationSource, mine, page, query, radius, selectedCoords, sort]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * HOW FAR "NEARBY" REACHES IS AN ADMIN SETTING, not a number in this file.
   *
   * `homepageSettings.publicSearchRadiusOptions` and `publicSearchDefaultRadiusMiles` already drive
   * `/shops`; the catalogue was using its own 5/10/25/50/100 list and a 25 default, so a change in
   * platform settings moved one page and not the other. Read once, and fall back to the same values
   * the server would use if this request fails.
   */
  useEffect(() => {
    let cancelled = false;
    const loadRadiusSettings = async () => {
      if (!apiBase) return;
      try {
        const res = await axios.get(`${apiBase}/api/admin/settings/public`);
        const settings = res.data?.settings?.homepageSettings || {};
        const rawOptions = Array.isArray(settings.publicSearchRadiusOptions)
          ? settings.publicSearchRadiusOptions
          : [];
        const options = [...new Set(rawOptions.map(Number).filter((n) => Number.isFinite(n) && n >= 1 && n <= 200))].sort(
          (a, b) => a - b,
        );
        const safeOptions = options.length ? options : [5, 10, 25, 50];
        const configured = Number(settings.publicSearchDefaultRadiusMiles);
        const nextDefault = safeOptions.includes(configured) ? configured : safeOptions[0];

        if (cancelled) return;
        setRadiusOptions(safeOptions);
        setDefaultRadius(String(nextDefault));
        // A radius in the URL wins — the visitor chose it.
        setRadius((current) => current || String(nextDefault));
      } catch {
        if (cancelled) return;
        setRadius((current) => current || "25");
      }
    };
    loadRadiusSettings();
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  /** Write the current view into the URL, so it can be reloaded and shared. */
  const applyToUrl = useCallback(
    (next) => {
      const params = new URLSearchParams();
      if (next.q) params.set("q", next.q);
      if (next.sort && next.sort !== "newest") params.set("sort", next.sort);
      if (next.mine) params.set("mine", "1");
      if (next.page && next.page > 1) params.set("page", String(next.page));
      if (next.radius) params.set("radius", String(next.radius));
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const applyFilters = useCallback(
    (overrides = {}) => {
      const next = {
        q: query.trim(),
        sort,
        mine,
        radius,
        page: 1,
        ...overrides,
      };
      setPage(next.page);
      applyToUrl(next);
    },
    [applyToUrl, mine, query, radius, sort],
  );

  const clearAll = () => {
    setQuery("");
    setSort("newest");
    setMine(false);
    // Back to the platform default, not to a number this file happens to like.
    setRadius(defaultRadius || radiusOptions[0]?.toString() || "");
    setPage(1);
    clearLocation();
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const openPackage = (pkg) => {
    // The package's own public purchase page — the same URL a shop copies to share (2.11).
    navigate(`/packages/${pkg._id}/purchase`);
  };

  const hasFilters = Boolean(query || selectedCoords || sort !== "newest" || mine);

  return (
    <div
      className="pk-shell"
      /*
        THE HEADER IS FIXED, so it takes no space in the flow and the demo banner above it takes none
        either — without this offset the page's first line sat underneath the navigation. Same
        arithmetic as the lookup and transfer pages: the banner publishes its own height, the header is
        50px tall (56px narrow), and 56 is used so the narrow case cannot clip.
      */
      style={{ paddingTop: "calc(var(--demo-banner-height, 0px) + 56px)" }}
    >
      <CustomerHeader />
      <div className="pk-page">
        <header className="pk-hero">
        <h1>Packages near you</h1>
        <p>
          Bundles of sessions at a lower price — from shops and professionals you can book with
          today. Pay once, then book each service in the package.
        </p>
      </header>

      <form
        className="pk-filters"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters();
        }}
      >
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search a package or a shop"
          aria-label="Search packages"
          className="pk-input"
        />

        {/*
          LOCATION — the same control as `/shops`: geolocation first, Places suggestions second.
          A suggestion is a place *object* (lat/lng included), not a typed word, which is what makes
          "nearest first" measurable instead of a name match.
        */}
        <div className="pk-location">
          <input
            type="text"
            value={locationInput}
            onChange={(event) => handleLocationInput(event.target.value)}
            onFocus={() => setShowSuggestions(true)}
            placeholder={
              geolocationStatus === "requesting" ? "Finding your location…" : "City or postcode"
            }
            aria-label="Your location"
            className="pk-input pk-input-city"
            autoComplete="off"
          />
          {selectedCoords ? (
            <button
              type="button"
              className="pk-location-clear"
              onClick={() => {
                clearLocation();
                applyFilters();
              }}
              aria-label="Clear location"
            >
              ✕
            </button>
          ) : null}
          {showSuggestions && suggestions.length > 0 ? (
            <ul className="pk-suggestions">
              {suggestions.map((suggestion) => (
                <li key={suggestion.place_id || suggestion.description}>
                  <button
                    type="button"
                    onClick={() => {
                      selectSuggestion(suggestion);
                      setShowSuggestions(false);
                    }}
                  >
                    {suggestion.description}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <select
          value={radius}
          onChange={(event) => {
            setRadius(event.target.value);
            applyFilters({ radius: event.target.value });
          }}
          aria-label="Search radius"
          className="pk-select pk-select-radius"
        >
          {radiusOptions.map((option) => (
            <option key={option} value={String(option)}>
              Within {option} mi
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(event) => {
            setSort(event.target.value);
            applyFilters({ sort: event.target.value });
          }}
          aria-label="Sort packages"
          className="pk-select"
        >
          <option value="newest">Newest</option>
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
          <option value="sessions-desc">Most sessions</option>
        </select>
        <button type="submit" className="pk-search-btn">
          Search
        </button>
      </form>

      <div className="pk-subfilters">
        {isSignedIn ? (
          <label className="pk-check">
            <input
              type="checkbox"
              checked={mine}
              onChange={(event) => {
                setMine(event.target.checked);
                applyFilters({ mine: event.target.checked });
              }}
            />
            Only shops I have booked or bought from
          </label>
        ) : null}
        {hasFilters ? (
          <button type="button" className="pk-clear" onClick={clearAll}>
            Clear filters
          </button>
        ) : null}
        {!loading && !error ? (
          <span className="pk-count">
            {total === 0 ? "No packages" : `${total} package${total === 1 ? "" : "s"}`}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="pk-state pk-state-error" role="alert">
          <strong>We could not load the packages.</strong>
          <p>{error}</p>
          <button type="button" className="pk-search-btn" onClick={load}>
            Try again
          </button>
        </div>
      ) : loading ? (
        <div className="pk-grid" aria-busy="true">
          {Array.from({ length: PAGE_SIZE }).map((_, index) => (
            <div key={index} className="pk-card pk-card-skeleton" />
          ))}
        </div>
      ) : packages.length === 0 ? (
        <div className="pk-state">
          <strong>
            {hasFilters ? "No packages match those filters." : "No packages are on sale yet."}
          </strong>
          <p>
            {hasFilters
              ? "Try a different city or a shorter search — or clear the filters to see everything."
              : "Shops add packages from their dashboard. Check back, or browse their services in the meantime."}
          </p>
          {hasFilters ? (
            <button type="button" className="pk-search-btn" onClick={clearAll}>
              Show all packages
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="pk-grid">
            {packages.map((pkg) => (
              <PackageCard key={pkg._id} pkg={pkg} onOpen={openPackage} />
            ))}
          </div>

          {pages > 1 ? (
            <nav className="pk-pager" aria-label="Package pages">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => applyFilters({ page: page - 1 })}
              >
                Previous
              </button>
              <span>
                Page {page} of {pages}
              </span>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => applyFilters({ page: page + 1 })}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}
      </div>
      <CustomerFooter />
    </div>
  );
}

export default PackageCatalogue;
